import type { QueryKey } from '@tanstack/react-query';
import type { Address } from 'app/types/address';
import type { BffResult } from 'app/types/bff';
import type { CustomChannelType } from 'app/types/chat';
import type { Group } from 'app/types/group';
import type { FindAllListingsDTO, FindListingsMapDTO, Listing } from 'app/types/listing';
import type { FindAllLoansDTO, Loan } from 'app/types/loan';
import type { GetAllPlansDTO } from 'app/types/plan';
import type { PreferenceName } from 'app/types/preferences';
import type { Profile } from 'app/types/profile';
import type { PostResponse } from 'app/types/post';
import type { QueryKeyParameters } from 'app/types/recommender';
import type { FindAllUsersDTO, User } from 'app/types/user';

export const addressKeys = {
  all: ['addresses'] as const,
  lists: () => [...addressKeys.all, 'list'] as const,
  list: (filters: string) => [...addressKeys.lists(), { filters }] as const,
  details: () => [...addressKeys.all, 'detail'] as const,
  detail: (id: Address['id']) => [...addressKeys.details(), id] as const,
};

export const bonificationKeys = {
  all: ['bonifications'] as const,
  details: () => [...bonificationKeys.all, 'detail'] as const,
  detail: (id: string) => [...bonificationKeys.details(), id] as const,
  pending: () => [...bonificationKeys.all, 'pending'] as const,
};

export const bffKeys = {
  all: ['bff'] as const,
  one: (url: string) => [...bffKeys.all, url] as const,
};

export const bffDiscoverQueryKeys = {
  get_categories: () => categoryKeys.list('all'),
  get_service_categories: () => categoryKeys.list('allServices'),
  get_popular_items: () => categoryKeys.list('popularItemsByCat'),
  get_top_listings: () => listingKeys.list('top'),
};

export const bffUserProfileQueryKeys: Record<string, (queryParams?: BffResult['queryParams']) => QueryKey> = {
  get_addresses: () => addressKeys.list('self'),
  get_chat_unread: () => messageKeys.unread(),
  get_groups: () => groupKeys.list('all'),
  get_loans: (queryParams: BffResult['queryParams']) => loanKeys.list('self', queryParams),
  get_num_unseen_notifications: () => notificationKeys.unread(),
  get_plans: () => planKeys.list('all'),
  get_preferences: () => preferencesKeys.details(),
  get_profiles: (queryParams: BffResult['queryParams']) => profileKeys.detail(queryParams?.userId as string),
  get_users: () => userKeys.detail('self'),
};

export const categoryKeys = {
  all: ['categories'] as const,
  lists: () => [...categoryKeys.all, 'list'] as const,
  list: (filters: string) => [...categoryKeys.lists(), { filters }] as const,
};

export const channelKeys = {
  all: ['channels'] as const,
  lists: () => [...channelKeys.all, 'list'] as const,
  list: (filters?: {
    search?: string;
    type?: CustomChannelType;
    archived?: boolean;
    listingId?: string;
    userId?: string;
  }) =>
    [
      ...channelKeys.lists(),
      {
        search: filters?.search || undefined,
        type: filters?.type,
        archived: filters?.archived,
        listingId: filters?.listingId,
        userId: filters?.userId,
      },
    ] as const,
  details: () => [...channelKeys.all, 'details'] as const,
  detailsForOne: (id: string) => [...channelKeys.details(), id] as const,
  detail: (id: string, include?: string[]) =>
    [...channelKeys.detailsForOne(id), ...(include ? [include.join(',')] : [])] as const,
};

export const interestKeys = {
  all: ['interests'] as const,
  lists: () => [...interestKeys.all, 'list'] as const,
  list: (name: string) => [...interestKeys.lists(), { name }] as const,
  details: () => [...interestKeys.all, 'detail'] as const,
  detail: (id: string) => [...interestKeys.details(), id] as const,
};

export const groupKeys = {
  all: ['groups'] as const,
  lists: () => [...groupKeys.all, 'list'] as const,
  list: (filters: string) => [...groupKeys.lists(), { filters }] as const,
  details: () => [...groupKeys.all, 'detail'] as const,
  detail: (id: Group['id']) => [...groupKeys.details(), id] as const,
  activitySectors: () => [...groupKeys.all, 'activity-sectors'] as const,
};

export const listingKeys = {
  all: ['listings'] as const,
  lists: () => [...listingKeys.all, 'list'] as const,
  list: (name: string, filters?: FindAllListingsDTO) => [...listingKeys.lists(), { name, filters }] as const,
  map: (filters?: FindListingsMapDTO) => [...listingKeys.lists(), { name: 'map', filters }] as const,
  details: () => [...listingKeys.all, 'detail'] as const,
  detail: (id: Listing['id']) => [...listingKeys.details(), id] as const,
  detailForScreen: (
    id: Listing['id'],
    screen: string,
    location?: Pick<FindAllListingsDTO, 'searchCenterLatitude' | 'searchCenterLongitude' | 'searchDistance'>,
  ) => {
    const hasLocation =
      location && Object.entries(location).some(([, value]: [string, unknown]) => value !== undefined);
    return [
      ...listingKeys.detail(id),
      ...(screen === 'one-listing' ? [] : [screen]),
      ...(hasLocation ? [location] : []),
    ] as const;
  },
  detailMany: (ids: string) => [...listingKeys.details(), 'multiple', ids] as const,
  bulkSessions: () => [...listingKeys.all, 'bulk-sessions'] as const,
  bulkSession: (id: string) => [...listingKeys.bulkSessions(), id] as const,
  lenders: (id: Listing['id']) => [...listingKeys.details(), id, 'lenders'] as const,
  borrowers: (id: Listing['id']) => [...listingKeys.details(), id, 'borrowers'] as const,
  comments: (id: Listing['id']) => [...listingKeys.details(), id, 'comments'] as const,
};

export const recommendationKeys = {
  all: ['recommendations'] as const,
  lists: () => [...recommendationKeys.all, 'list'] as const,
  list: (name: string, parameters?: QueryKeyParameters) =>
    [...recommendationKeys.lists(), { name, parameters }] as const,
};

export const loanKeys = {
  all: ['loans'] as const,
  lists: () => [...loanKeys.all, 'list'] as const,
  list: (name: string, filters?: FindAllLoansDTO) => [...loanKeys.lists(), { name, filters }] as const,
  ratings: (filters?: { userId?: User['id']; listingId?: Listing['id']; reviewerId?: User['id'] }) => {
    const normalizedFilters = filters ?? {};
    return [
      ...loanKeys.lists(),
      {
        name: 'ratings',
        userId: normalizedFilters.userId,
        listingId: normalizedFilters.listingId,
        reviewerId: normalizedFilters.reviewerId,
      },
    ] as const;
  },
  details: () => [...loanKeys.all, 'detail'] as const,
  detail: (id: Loan['id']) => [...loanKeys.details(), id] as const,
};

export const messageKeys = {
  all: ['messages'] as const,
  unread: () => [...messageKeys.all, 'details', 'unread'] as const,
};

export const monthlyStatsKeys = {
  all: ['monthly-stats'] as const,
  list: () => [...monthlyStatsKeys.all, 'list'] as const,
  detail: (period: string) => [...monthlyStatsKeys.all, 'detail', period] as const,
  members: (period: string, take: number) => [...monthlyStatsKeys.all, 'members', period, take] as const,
};

export const notificationKeys = {
  all: ['notifications'] as const,
  unread: () => [...notificationKeys.all, 'unread'] as const,
  details: () => [...notificationKeys.all, 'detail'] as const,
  detail: (id: User['id'], markAsRead: boolean) => [...notificationKeys.details(), id, markAsRead] as const,
};

export const postKeys = {
  all: ['posts'] as const,
  list: () => [...postKeys.all, 'list'] as const,
  details: () => [...postKeys.all, 'detail'] as const,
  detail: (id: PostResponse['id']) => [...postKeys.details(), id] as const,
  comments: (id: PostResponse['id']) => [...postKeys.detail(id), 'comments'] as const,
  byLoan: (loanId: string) => [...postKeys.all, 'loan', loanId] as const,
  byListing: (listingId: string) => [...postKeys.all, 'listing', listingId] as const,
  byUser: (userId: string) => [...postKeys.all, 'user', userId] as const,
};

export const planKeys = {
  all: ['plans'] as const,
  lists: () => [...planKeys.all, 'list'] as const,
  list: (name: string, filters?: GetAllPlansDTO) => [...planKeys.lists(), { name, filters }] as const,
};

export const preferencesKeys = {
  all: ['preferences'] as const,
  details: () => [...preferencesKeys.all, 'detail'] as const,
  detail: (name: PreferenceName) => [...preferencesKeys.details(), name] as const,
};

export const profileKeys = {
  all: ['profiles'] as const,
  lists: () => [...profileKeys.all, 'list'] as const,
  list: (filters: string) => [...profileKeys.lists(), { filters }] as const,
  details: () => [...profileKeys.all, 'detail'] as const,
  detail: (id: Profile['id']) => [...profileKeys.details(), id] as const,
  privateDetail: (id: Profile['id']) => [...profileKeys.detail(id), 'private'] as const,
};

export const sharingPlaceKeys = {
  all: ['sharing-places'] as const,
  myPlaces: () => [...sharingPlaceKeys.all, 'my-places'] as const,
  members: (placeId: string) => [...sharingPlaceKeys.all, placeId, 'members'] as const,
};

export const userKeys = {
  all: ['users'] as const,
  typeConfig: () => [...userKeys.all, 'typeConfig'] as const,
  lists: () => [...userKeys.all, 'list'] as const,
  list: (filters?: FindAllUsersDTO) => [...userKeys.lists(), { filters }] as const,
  invites: () => [...userKeys.all, 'invites'] as const,
  details: () => [...userKeys.all, 'detail'] as const,
  detail: (id: User['id']) => [...userKeys.details(), id] as const,
  dailyStats: () => [...userKeys.all, 'daily-stats'] as const,
};
